/**
 * Shop Pay Installments — "Pay in 3 interest-free instalments of £X with Shop Pay".
 *
 * Shopify renders <shopify-payment-terms> into a shadow root, so no theme stylesheet
 * can reach its contents. Left alone the widget uses Shopify's own
 * `GTStandard-M, sans-serif` at 16px/24px, which reads as visibly off-brand sitting
 * directly under the buy button. This restores the theme's body font at --text-sm.
 *
 * LOADING
 * `snippets/scripts.liquid` imports this module on product templates only, and even
 * there only once `customElements.whenDefined('shopify-payment-terms')` resolves.
 * Shopify only loads the module defining that element on pages which actually render
 * the widget (today: products with a ring size option), so on every other page this
 * file is never fetched, parsed or executed.
 *
 * Cart and cart-drawer instalment widgets are deliberately out of scope and keep
 * Shopify's default styling.
 *
 * MECHANISM
 * A single constructed stylesheet, shared by every instance and attached through
 * `adoptedStyleSheets`. Because it is not a child node of the shadow root, it survives
 * Shopify re-rendering the widget's contents (which happens once instalment pricing
 * resolves) and therefore needs no re-injection watcher. Engines without constructable
 * stylesheets (Safari < 16.4) fall back to injecting a <style> node, which *can* be
 * dropped by that re-render and so keeps a small watcher of its own.
 */

const SELECTOR = 'shopify-payment-terms';
const STYLE_ID = 'theme-payment-terms-style';

const CSS = `
  #shopify-installments,
  #shopify-installments * {
    font-family: var(--font-body) !important;
    font-size: var(--text-sm) !important;
    line-height: var(--tw-leading, var(--text-sm--line-height)) !important;
  }
`;

/**
 * Parsed once and reused by every widget on the page.
 * @type {CSSStyleSheet | null}
 */
const sharedSheet = (() => {
  if (typeof CSSStyleSheet !== 'function') return null;

  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(CSS);
    return sheet;
  } catch {
    return null;
  }
})();

/** Shadow roots already given a <style> fallback watcher. @type {WeakSet<ShadowRoot>} */
const watched = new WeakSet();

/** @param {ShadowRoot} shadowRoot */
const injectStyleNode = (shadowRoot) => {
  if (shadowRoot.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  shadowRoot.appendChild(style);
};

/** @param {ShadowRoot | null} shadowRoot */
const applyTheme = (shadowRoot) => {
  if (!shadowRoot) return;

  if (sharedSheet && Array.isArray(shadowRoot.adoptedStyleSheets)) {
    if (!shadowRoot.adoptedStyleSheets.includes(sharedSheet)) {
      shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sharedSheet];
    }
    return;
  }

  injectStyleNode(shadowRoot);

  if (watched.has(shadowRoot)) return;
  watched.add(shadowRoot);

  new MutationObserver(() => injectStyleNode(shadowRoot)).observe(shadowRoot, { childList: true });
};

/**
 * The shadow root can be attached a tick or two after the element upgrades, so retry
 * for a short bounded window rather than giving up on the first miss.
 *
 * @param {Element} element
 * @param {number} [attempt]
 */
const enhance = (element, attempt = 0) => {
  if (!(element instanceof HTMLElement)) return;

  if (element.shadowRoot) {
    applyTheme(element.shadowRoot);
    return;
  }

  if (attempt < 10) requestAnimationFrame(() => enhance(element, attempt + 1));
};

/** @param {Node} node */
const scan = (node) => {
  if (!(node instanceof Element) && node !== document) return;

  if (node instanceof Element && node.matches(SELECTOR)) enhance(node);

  /** @type {ParentNode} */ (node).querySelectorAll(SELECTOR).forEach(enhance);
};

scan(document);

/**
 * The widget can also appear after load — a variant change re-rendering the buy
 * buttons, or the cart drawer opening on this product page. Only ever reached on a
 * product page that already loaded the widget, never on collection or content templates.
 */
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) scan(node);
  }
}).observe(document.body, { childList: true, subtree: true });
